import type { AuthId, CalendarId, EventId } from "@tento-lona/sheets";
import type { Option } from "@tento-lona/sheets";
import type { DateTime, DateUnit, NaiveDate, Utc } from "@tento-chrono";
import {
  EventItem,
  EventItemAllDay,
  EventItemTimed,
} from "../events/event-item.ts";
import type { EventOps } from "../indices/event-ops.ts";
import { EventWriteError } from "../errors.ts";
import type { Network } from "../network.ts";
import type {
  Actor,
  CalendarEvent as SheetsEvent,
  EventItem as SheetsEventItem,
  EventItemAllDay as SheetsEventItemAllDay,
  EventItemTimed as SheetsEventItemTimed,
  RowLocalId,
} from "@tento-lona/sheets";
import { EventOrigin } from "@tento-lona/sheets";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import { UtcDays } from "@tento-lona/sheets/internal-sdk";
import { Tracing } from "@tento-core/tracing";
import { EVENT_SHAPE } from "@tento-lona/sheets/platform";
import { EventsIndex } from "@tento-lona/sheets/internal-ui";
import { EventsFacade } from "@tento-lona/sheets/platform";

/** Range loader thunk — wired by `row.events()` to the row's
 *  rows-side `variant.ensureLoaded`. Null on standalone rows that
 *  carry no variant. */
export type RowEventsLoader = (
  range: NaiveDate.Range,
  padding: Option<DateUnit>,
) => Promise<void>;

/**
 * Read backing for `RowEvents`. Both Joined.Handle (facade-backed)
 * and standalone Client (RowCellCache-backed) supply one of these.
 * Returns sheets-side EventItem types; `RowEvents` re-wraps to the
 * SDK-side enriched type at the call site.
 */
export interface RowEventsReader {
  peek(id: string): Option<SheetsEventItem>;
  has(id: string): boolean;
  peekInRange(
    range: DateTime.Range<Utc>,
  ): readonly SheetsEventItemTimed[];
  peekAllDayInRange(
    range: NaiveDate.Range,
  ): readonly SheetsEventItemAllDay[];
  peekOnDate(dt: DateTime<Utc>): readonly SheetsEventItemTimed[];
  /** Mint this index's synthetic RRULE instances up to `until` (exclusive), so
   *  a SYNCHRONOUS range read after it can see a mid-series occurrence. The
   *  read queries above only kick expansion asynchronously (or, on the
   *  RowCellCache-backed reader, not at all), which is right for the renderer —
   *  it repaints on the expander's announce — but useless to a find-then-act
   *  caller that scans once and returns. Required, not optional: a reader that
   *  silently skipped it would make such a caller a no-op at runtime. */
  expandRecurrenceUntil(until: NaiveDate): Promise<void>;
}

/** Empty reader — used for rows with no facade and no rowHandle.
 *  All queries return empty; mutations still work via `EventOps`
 *  but `RowEvents.requireEvent` will throw because the index has
 *  no events to find. */
export const EMPTY_ROW_EVENTS_READER: RowEventsReader = {
  peek: () => null,
  has: () => false,
  peekInRange: () => [],
  peekAllDayInRange: () => [],
  peekOnDate: () => [],
  expandRecurrenceUntil: () => Promise.resolve(),
};

/**
 * Reader backed by a `RowCellCache.allCells()` stream. Used by standalone
 * `SheetRow.Handle.events()` where no sheets-side `SourceRow` is
 * available to host an `EVENTS_FACADE`. Mirrors the dirty-rebuild
 * + content-hash short-circuit pattern from `EventsFacade`.
 *
 * Why this is a read model of its own and not the facade: its cells live in
 * the SDK's per-handle `RowCellCache`, not in a sheet's cell stores, so there
 * is no `SourceRow` (no `ColumnDataChanged`, no `eventOrigin`) a facade could
 * attach to. It shares the facade's index (`EventsIndex`), event shape probe
 * and signature hash, so membership, recurrence expansion and EXDATE
 * handling are the facade's; only the change source (`rh.onChange`) and the
 * no-op sink (nothing renders a standalone row) differ. The calendar read
 * (`calendarEventRead`) never consults it: a rendered calendar row always has
 * a sheet. A standalone row that ever gains a `SourceRow` should read
 * through `EVENTS_FACADE` instead of this.
 */
export class RowHandleEventsReader implements RowEventsReader {
  readonly #index: EventsIndex;
  #dirty = true;
  #lastPopulatedHash: Option<string> = null;

  readonly #rh: RowCellCache;
  constructor(
    rh: RowCellCache,
    rowId: RowLocalId,
  ) {
    // Standalone-Client reads have no downstream UI subscribing to
    // `ViewportsInvalidated` / `TimepointsChanged`; pass a no-op
    // sink so `EventsIndex.populate` doesn't try to wake observers
    // that aren't there.
    this.#rh = rh;
    this.#index = new EventsIndex({ emit: () => {} }, EventOrigin.backingRow(rowId));
    rh.onChange(() => {
      this.#dirty = true;
    });
  }

  peek(id: EventId): Option<SheetsEventItem> {
    this.#refreshIfDirty();
    return this.#index.peek(id);
  }

  has(id: EventId): boolean {
    this.#refreshIfDirty();
    return this.#index.has(id);
  }

  peekOnDate(dt: DateTime<Utc>): readonly SheetsEventItemTimed[] {
    this.#refreshIfDirty();
    return this.#index.peekOnDate(dt);
  }

  peekInRange(
    range: DateTime.Range<Utc>,
  ): readonly SheetsEventItemTimed[] {
    this.#refreshIfDirty();
    return this.#index.peekInRange(range);
  }

  peekAllDayInRange(
    range: NaiveDate.Range,
  ): readonly SheetsEventItemAllDay[] {
    this.#refreshIfDirty();
    return this.#index.peekAllDayInRange(range);
  }

  /** `announce: false` — this reader's sink is a no-op and nothing subscribes
   *  to it, so there is no one to wake. */
  async expandRecurrenceUntil(until: NaiveDate): Promise<void> {
    this.#refreshIfDirty();
    await this.#index.expandRecurrenceUntil(until, "silent");
  }

  #refreshIfDirty(): void {
    if (!this.#dirty) return;
    const events: SheetsEvent[] = [];
    // Both lanes: datetime-keyed cells live in the custom store — fetched
    // timed events always land there, and on multi-cardinality event rows
    // (the `calendar-events` source row) EVERY event cell does. Without them
    // the reader's index misses those events and `RowEvents.requireEvent`
    // throws "event not found" before a delete/update can even run — the
    // standalone-row write path the calendar UI uses
    // (`sdk.rows.open(origin).events().delete(...)`).
    for (const cell of this.#rh.allCells()) {
      if (EVENT_SHAPE.probe(cell.value)) events.push(cell.value);
    }
    const hash = EventsFacade.hashEvents(events);
    if (hash === this.#lastPopulatedHash) {
      this.#dirty = false;
      return;
    }
    this.#index.populate(events);
    this.#lastPopulatedHash = hash;
    this.#dirty = false;
  }
}

/** Assigned from `RowEvents`' static block; see {@link rowEventsOptimistic}. */
let optimisticOf: (events: RowEvents) => RowEvents.Optimistic;

/**
 * The create seams of `events` (`RowEvents.Optimistic`): the placeholder a
 * create paints before its request, the create itself (its ack reconciles
 * the placeholder), and the placeholder's local drop when the create fails.
 * SDK-private — `EventsClient.create` and the occurrence gestures own the
 * placeholder's lifecycle; a caller creates through
 * `client.events.create(...)`.
 *
 * @internal Not exported from `lib.ts`.
 */
export function rowEventsOptimistic(events: RowEvents): RowEvents.Optimistic {
  return optimisticOf(events);
}

/**
 * A row's events, READ-ONLY: the cell-derived index (`peek`, `has`,
 * `peekInRange`, `peekAllDayInRange`, `peekOnDate`), the recurrence
 * expansion a synchronous read needs first (`expandRecurrenceUntil`), the
 * range load that fills it (`ensureLoaded`), and `peekInTable` (an id → the
 * held item, through the client's `EventsTable`, which also holds a
 * recurrence master this row's own index does not).
 *
 * Obtained via `row.events()` on `SheetRow.Handle` /
 * `SheetRow.Joined.Handle`. Lazy-instantiated on the first call and cached
 * on the row.
 *
 * Event writes are the client's: `client.events.create` / `update` /
 * `updateDurable` / `delete` / `moveTo` / `copyTo` and the occurrence verbs.
 * They address the write from the held item's `origin`, so an event that
 * renders under a projection row (a calendar-list aggregate) or has not
 * entered this row's reader yet writes like any other.
 *
 * Every row is potentially events-bearing: the index is just an
 * `EVENT_SHAPE` probe over `row.peekCells(null)`. Rows with no matching
 * cells produce empty queries.
 */
export class RowEvents {
  static {
    optimisticOf = (events) => ({
      local: {
        insert: (authId, calendarId, event, actor) =>
          events.#insertOptimistic(authId, calendarId, event, actor),
        remove: (placeholder) => events.#removeLocal(placeholder),
      },
      commit: (authId, calendarId, event, reconcile, actor) =>
        events.#requireOps("create").create(authId, calendarId, event, reconcile, actor),
    });
  }

  readonly #loader: Option<RowEventsLoader>;
  readonly #opsProvider: () => Option<EventOps>;
  readonly #reader: RowEventsReader;
  constructor(
    reader: RowEventsReader,
    opsProvider: () => Option<EventOps>,
    loader: Option<RowEventsLoader>,
  ) {
    this.#reader = reader;
    this.#opsProvider = opsProvider;
    this.#loader = loader;
  }

  // ── Reads (sync — backed by the cell-derived reader index) ──────
  //
  // The reader returns sheets-side `EventItem` (raw indexed events).
  // RowEvents re-wraps via `EventItem.make` so renderer consumers
  // get the SDK-side enriched type (`rowProps`, `acid`, `acal`, …).

  peek(id: EventId): Option<EventItem> {
    const raw = this.#reader.peek(id);
    return raw
      ? EventItem.make(raw.inner, raw.recurrenceInfo, raw.origin)
      : null;
  }

  has(id: EventId): boolean {
    return this.#reader.has(id);
  }

  peekInRange(
    range: DateTime.Range<Utc>,
  ): readonly EventItemTimed[] {
    const raws = this.#reader.peekInRange(range);
    const out: EventItemTimed[] = [];
    for (const raw of raws) {
      const wrapped = EventItem.make(raw.inner, raw.recurrenceInfo, raw.origin);
      if (wrapped instanceof EventItemTimed) out.push(wrapped);
    }
    return out;
  }

  peekAllDayInRange(
    range: NaiveDate.Range,
  ): readonly EventItemAllDay[] {
    const raws = this.#reader.peekAllDayInRange(range);
    const out: EventItemAllDay[] = [];
    for (const raw of raws) {
      const wrapped = EventItem.make(raw.inner, raw.recurrenceInfo, raw.origin);
      if (wrapped instanceof EventItemAllDay) out.push(wrapped);
    }
    return out;
  }

  peekOnDate(dt: DateTime<Utc>): readonly EventItemTimed[] {
    const raws = this.#reader.peekOnDate(dt);
    const out: EventItemTimed[] = [];
    for (const raw of raws) {
      const wrapped = EventItem.make(raw.inner, raw.recurrenceInfo, raw.origin);
      if (wrapped instanceof EventItemTimed) out.push(wrapped);
    }
    return out;
  }

  /** Mint the synthetic RRULE instances covering up to `until` (exclusive)
   *  before a synchronous read. `peekInRange` alone returns only REAL cells
   *  — the master and any server-materialized override — so a lookup on a day
   *  after the master's DTSTART matches nothing while the occurrence is
   *  visibly painted. Callers that scan-then-act on a mid-series occurrence
   *  (the this/following split) must await this first. */
  async expandRecurrenceUntil(until: NaiveDate): Promise<void> {
    await this.#reader.expandRecurrenceUntil(until);
  }

  // ── Load (network — populates cells the index reads from) ───────

  /**
   * Fetch events overlapping `range` into the row's cell store.
   * Delegates to the rows-side `variant.ensureLoaded`; subsequent
   * sync reads (`peekInRange` / `peekOnDate`) see the new cells
   * via `ColumnDataChanged` → facade dirty-rebuild.
   *
   * No-op when the row carries no variant (e.g. standalone
   * `SheetRow.Handle` opened via `sdk.rows.open`). Standalone-row
   * loading flows still need to use `RowCellCache.fetcher.fetchRange`
   * directly until Phase 5 collapses both paths.
   */
  /** Watchdog catches stuck-fetcher hangs (5s is generous for normal
   *  range loads). Shared across all RowEvents instances.
   *
   *  No surrounding `Tracing.span` — `ensureLoaded()` fires on every visible-
   *  column refresh (sometimes hundreds per page bind), and a span
   *  per call drowns the LJSON stream. The watchdog still covers the
   *  hang case loudly. Add a span back at the call site if you need
   *  to correlate a *specific* load with a parent user action. */
  static readonly #loadWatchdog = new Tracing.Witness.Watchdog({
    name: "row-events/load",
  });

  async ensureLoaded(
    range: DateTime.Range<Utc>,
    padding: Option<DateUnit>,
  ): Promise<void> {
    if (!this.#loader) return;
    // Every UTC day the range touches: a viewer day anywhere but UTC ends past a UTC midnight.
    const naiveRange = UtcDays.touched(range);
    const disarm = RowEvents.#loadWatchdog.arm(5000, {
      rangeStart: naiveRange.start.toString(),
      rangeEnd: naiveRange.end.toString(),
    });
    try {
      await this.#loader(naiveRange, padding);
    } finally {
      disarm();
    }
  }

  /**
   * The event `id` names, through the client `EventsTable` (the single
   * id→item authority), or `null`. A read: resolve the item, then hand it to
   * a write (`client.events.update(item, …)`, `delete(item, …)`). Also resolves a
   * recurrence MASTER from a clicked occurrence, which frequently lives on a
   * different source row than the one the occurrence renders under (a
   * per-row reader would miss it).
   */
  peekInTable(id: EventId): Option<EventItem> {
    return this.#opsProvider()?.itemFor(id) ?? null;
  }

  /** `RowEvents.Optimistic.local.insert` — see `rowEventsOptimistic`. */
  #insertOptimistic(
    authId: AuthId,
    calendarId: CalendarId,
    event: Network.NewEvent,
    actor: Actor,
  ): EventItem {
    return this.#requireOps("create").insertOptimistic(authId, calendarId, event, actor);
  }

  /** `RowEvents.Optimistic.local.remove` — see `rowEventsOptimistic`. */
  #removeLocal(placeholder: EventItem): void {
    this.#requireOps("delete").removeLocal(placeholder);
  }

  // `EventOps` requires the row's `RowCellCache` to be attached to an
  // `EventsTable`. Standalone rows opened via `sdk.rows.open` get
  // attached lazily on first `eventOps` access; rows owned by a
  // sheet tree get attached during `RowsClient.onRowHandleInit`.
  // A row that never went through that path (a wiring bug) cannot address
  // the write: `EventWriteError` with `outcome: "unaddressable"`, never a
  // silently no-op'd mutation.
  #requireOps(step: EventWriteError.Step): EventOps {
    const ops = this.#opsProvider();
    if (ops == null) {
      // Never sent: the row cannot address an event write.
      throw new EventWriteError({
        step,
        method: step === "create" || step === "copy-to" ? "POST" : "PATCH",
        path: "/api/rows/{rowKey}/cells",
        failure: { outcome: "unaddressable" },
        details: `RowEvents.${step}: no EventOps for this row — its RowCellCache ` +
          `is not attached to an EventsTable (a wiring bug)`,
      });
    }
    return ops;
  }
}

/** Types that go with `RowEvents`: `Optimistic`. */
export namespace RowEvents {
  /**
   * The create seams (`rowEventsOptimistic`). SDK-private. The local-only
   * edits are on `local`, as `platformOf(handle).local`'s are.
   */
  export interface Optimistic {
    /** The placeholder's local-only edits: nothing is sent. */
    readonly local: Optimistic.Local;
    /**
     * Send the create. `reconcile` is the placeholder `local.insert` painted (its
     * ack replaces it atomically), or `null` for a create with none (a
     * recurrence fork). Rejects with `EventWriteError`.
     */
    commit(
      authId: AuthId,
      calendarId: CalendarId,
      event: Network.NewEvent,
      reconcile: Option<EventItem>,
      actor: Actor,
    ): Promise<EventItem>;
  }

  export namespace Optimistic {
    /** `Optimistic.local`: a create's placeholder, painted and dropped locally. */
    export interface Local {
      /** Paint a placeholder for `event` in `[authId, calendarId]`, stamped `actor`. */
      insert(
        authId: AuthId,
        calendarId: CalendarId,
        event: Network.NewEvent,
        actor: Actor,
      ): EventItem;
      /**
       * Drop `placeholder` (what `insert` returned) from every local store —
       * from the calendar row it was painted on — stamped with the actor
       * that painted it (its author's own withdrawal).
       */
      remove(placeholder: EventItem): void;
    }
  }
}
